import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'

import {
  bookingAvailability,
  createBooking,
  listBusinessPublicBookingServices,
  listProPublicBookingServices,
  quoteBooking,
} from '../../api/bookings'
import { addDaysIso, getMonthDates, monthStartIso, todayIsoDate } from '../../lib/bookingCalendar'
import BookingCalendar from './BookingCalendar'
import BookingDaySlots from './BookingDaySlots'
import NextAvailableButton from './NextAvailableButton'
import type {
  Booking,
  BookingQuoteResponse,
  BookingService,
  BookingSlot,
  BookingStaffMember,
} from '../../types/bookings'

const stripePublicKey = (import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY || ''
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null

type Props = {
  open: boolean
  hostType: 'business' | 'pro'
  hostRef: string
  displayName: string
  onClose: () => void
  onBooked?: (booking: Booking) => void
}

type DaySummary = {
  availableCount: number
  enabled: boolean
  closed: boolean
  reasonCode: string | null
  loading?: boolean
}

function DiscoverBookingPaymentConfirm({
  onSuccess,
  onError,
  busy,
  setBusy,
}: {
  onSuccess: () => void
  onError: (message: string) => void
  busy: boolean
  setBusy: (value: boolean) => void
}) {
  const stripe = useStripe()
  const elements = useElements()

  const onConfirm = async () => {
    if (!stripe || !elements || busy) return
    setBusy(true)
    onError('')
    const result = await stripe.confirmPayment({ elements, redirect: 'if_required' })
    if (result.error) {
      onError(result.error.message || 'Payment confirmation failed.')
      setBusy(false)
      return
    }
    setBusy(false)
    onSuccess()
  }

  return (
    <div className="space-y-3">
      <PaymentElement />
      <button
        type="button"
        onClick={onConfirm}
        disabled={busy || !stripe || !elements}
        className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
      >
        {busy ? 'Processing...' : 'Pay deposit'}
      </button>
    </div>
  )
}

export default function DiscoverBookingModal({
  open,
  hostType,
  hostRef,
  displayName,
  onClose,
  onBooked,
}: Props) {
  const [services, setServices] = useState<BookingService[]>([])
  const [serviceId, setServiceId] = useState<number | null>(null)
  const [selectedDate, setSelectedDate] = useState(todayIsoDate())
  const [visibleMonth, setVisibleMonth] = useState(monthStartIso(todayIsoDate()))
  const [slots, setSlots] = useState<BookingSlot[]>([])
  const [selectedSlot, setSelectedSlot] = useState<BookingSlot | null>(null)
  const [daySummaries, setDaySummaries] = useState<Record<string, DaySummary>>({})
  const [availabilityCache, setAvailabilityCache] = useState<Record<string, BookingSlot[]>>({})
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [findingNext, setFindingNext] = useState(false)
  const [bookingError, setBookingError] = useState<string | null>(null)
  const [bookingSuccess, setBookingSuccess] = useState<Booking | null>(null)
  const [bookingQuote, setBookingQuote] = useState<BookingQuoteResponse | null>(null)
  const [bookingClientSecret, setBookingClientSecret] = useState('')
  const [bookingSubmitting, setBookingSubmitting] = useState(false)
  const [paymentSubmitting, setPaymentSubmitting] = useState(false)
  const [staffMode, setStaffMode] = useState<'ANY' | 'SPECIFIC'>('ANY')
  const [staffId, setStaffId] = useState<number | null>(null)
  const [staffList, setStaffList] = useState<BookingStaffMember[]>([])
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [notes, setNotes] = useState('')

  const resetState = useCallback(() => {
    const today = todayIsoDate()
    setServices([])
    setServiceId(null)
    setSelectedDate(today)
    setVisibleMonth(monthStartIso(today))
    setSlots([])
    setSelectedSlot(null)
    setDaySummaries({})
    setAvailabilityCache({})
    setAvailabilityLoading(false)
    setFindingNext(false)
    setBookingError(null)
    setBookingSuccess(null)
    setBookingQuote(null)
    setBookingClientSecret('')
    setBookingSubmitting(false)
    setPaymentSubmitting(false)
    setStaffMode('ANY')
    setStaffId(null)
    setStaffList([])
    setName('')
    setEmail('')
    setPhone('')
    setNotes('')
  }, [])

  useEffect(() => {
    if (!open) return
    resetState()
  }, [open, resetState])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const loadServices = async () => {
      try {
        const rows = hostType === 'business'
          ? await listBusinessPublicBookingServices(hostRef)
          : await listProPublicBookingServices(hostRef)
        if (cancelled) return
        const active = rows.filter((row) => row.is_active)
        setServices(active)
        setServiceId(active[0]?.id ?? null)
      } catch (e: any) {
        if (!cancelled) setBookingError(e?.response?.data?.detail || 'Failed to load services.')
      }
    }
    void loadServices()
    return () => {
      cancelled = true
    }
  }, [hostRef, hostType, open])

  const loadDay = useCallback(async (isoDate: string) => {
    if (!open || !serviceId) return
    setAvailabilityLoading(true)
    setBookingError(null)
    try {
      const row = await bookingAvailability({
        host_type: hostType,
        ...(hostType === 'business' ? { slug: hostRef } : { handle: hostRef }),
        date: isoDate,
        service_id: serviceId,
        staff_mode: staffMode,
        staff_id: staffMode === 'SPECIFIC' ? (staffId ?? undefined) : undefined,
      })
      const nextSlots = Array.isArray(row.slots) ? row.slots : []
      setSlots(nextSlots)
      setAvailabilityCache((prev) => ({ ...prev, [isoDate]: nextSlots }))
      setDaySummaries((prev) => ({
        ...prev,
        [isoDate]: {
          availableCount: nextSlots.length,
          enabled: row.enabled,
          closed: row.closed,
          reasonCode: row.reason_code || null,
        },
      }))
      if (Array.isArray(row.staff_list)) {
        setStaffList(row.staff_list)
        if (row.staff_mode === 'SPECIFIC') {
          setStaffMode('SPECIFIC')
          const firstStaffId = row.staff_list[0]?.id ?? null
          setStaffId((current) => current ?? firstStaffId)
        }
      }
    } catch (e: any) {
      setSlots([])
      setBookingError(e?.response?.data?.detail || 'Failed to load availability.')
    } finally {
      setAvailabilityLoading(false)
    }
  }, [hostRef, hostType, open, serviceId, staffId, staffMode])

  useEffect(() => {
    if (!open || !serviceId) return
    setSelectedSlot(null)
    void loadDay(selectedDate)
  }, [loadDay, open, selectedDate, serviceId, staffMode, staffId])

  useEffect(() => {
    if (!open || !serviceId) return
    quoteBooking({
      host_type: hostType,
      host_ref: hostRef,
      service_id: serviceId,
    })
      .then((quote) => setBookingQuote(quote))
      .catch(() => setBookingQuote(null))
  }, [hostRef, hostType, open, serviceId])

  useEffect(() => {
    if (!open || !serviceId) return
    const monthDays = getMonthDates(visibleMonth)
    if (!monthDays.length) return
    let cancelled = false
    const hydrateMonth = async () => {
      const firstBatch = monthDays.slice(0, 14)
      for (const day of firstBatch) {
        if (cancelled) return
        if (availabilityCache[day]) {
          setDaySummaries((prev) => ({
            ...prev,
            [day]: {
              availableCount: availabilityCache[day].length,
              enabled: true,
              closed: false,
              reasonCode: null,
            },
          }))
          continue
        }
        try {
          const row = await bookingAvailability({
            host_type: hostType,
            ...(hostType === 'business' ? { slug: hostRef } : { handle: hostRef }),
            date: day,
            service_id: serviceId,
          })
          const count = Array.isArray(row.slots) ? row.slots.length : 0
          setDaySummaries((prev) => ({
            ...prev,
            [day]: {
              availableCount: count,
              enabled: row.enabled,
              closed: row.closed,
              reasonCode: row.reason_code || null,
            },
          }))
          setAvailabilityCache((prev) => ({ ...prev, [day]: row.slots || [] }))
        } catch {
          setDaySummaries((prev) => ({
            ...prev,
            [day]: {
              availableCount: 0,
              enabled: false,
              closed: true,
              reasonCode: 'NO_HOURS',
            },
          }))
        }
      }
    }
    void hydrateMonth()
    return () => {
      cancelled = true
    }
  }, [availabilityCache, hostRef, hostType, open, serviceId, visibleMonth])

  const findNextAvailable = useCallback(async () => {
    if (!serviceId) return
    setFindingNext(true)
    setBookingError(null)
    try {
      for (let i = 0; i < 30; i += 1) {
        const day = addDaysIso(todayIsoDate(), i)
        const cached = availabilityCache[day]
        if (cached && cached.length > 0) {
          setSelectedDate(day)
          setVisibleMonth(monthStartIso(day))
          setSlots(cached)
          return
        }
        const row = await bookingAvailability({
          host_type: hostType,
          ...(hostType === 'business' ? { slug: hostRef } : { handle: hostRef }),
          date: day,
          service_id: serviceId,
          staff_mode: staffMode,
          staff_id: staffMode === 'SPECIFIC' ? (staffId ?? undefined) : undefined,
        })
        const nextSlots = row.slots || []
        setAvailabilityCache((prev) => ({ ...prev, [day]: nextSlots }))
        setDaySummaries((prev) => ({
          ...prev,
          [day]: {
            availableCount: nextSlots.length,
            enabled: row.enabled,
            closed: row.closed,
            reasonCode: row.reason_code || null,
          },
        }))
        if (nextSlots.length > 0) {
          setSelectedDate(day)
          setVisibleMonth(monthStartIso(day))
          setSlots(nextSlots)
          return
        }
      }
      setBookingError('No upcoming availability in the next 30 days.')
    } finally {
      setFindingNext(false)
    }
  }, [availabilityCache, hostRef, hostType, serviceId, staffId, staffMode])

  const canSubmit = useMemo(() => {
    return Boolean(selectedSlot && name.trim() && email.trim())
  }, [email, name, selectedSlot])

  const onSubmit = async () => {
    if (!selectedSlot || !canSubmit || bookingSubmitting) return
    setBookingSubmitting(true)
    setBookingError(null)
    try {
      const created = await createBooking({
        host_type: hostType,
        host_ref: hostRef,
        starts_at: selectedSlot.starts_at,
        service_id: serviceId,
        staff_mode: staffMode,
        staff_id: staffMode === 'SPECIFIC' ? staffId : null,
        customer_name: name.trim(),
        customer_email: email.trim(),
        customer_phone: phone.trim() || undefined,
        notes: notes.trim() || undefined,
      })
      if (created.client_secret) {
        setBookingClientSecret(created.client_secret)
        setBookingSuccess(created)
      } else {
        setBookingSuccess(created)
        window.setTimeout(() => {
          onBooked?.(created)
        }, 900)
      }
    } catch (e: any) {
      setBookingError(e?.response?.data?.detail || e?.message || 'Failed to create booking.')
    } finally {
      setBookingSubmitting(false)
    }
  }

  const onPaymentSuccess = async () => {
    if (!bookingSuccess) return
    setBookingClientSecret('')
    window.setTimeout(() => {
      onBooked?.(bookingSuccess)
    }, 900)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-4xl rounded-2xl border border-white/15 bg-[#0f1014] p-4 shadow-2xl">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-extrabold text-white">Book {displayName}</h3>
            <p className="text-xs text-white/60">Choose a service, time, and complete booking from discover.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white hover:bg-white/10"
          >
            Close
          </button>
        </div>

        {bookingSuccess && !bookingClientSecret ? (
          <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-100">
            Booking confirmed.
            {' '}
            <Link to={`/booking/${bookingSuccess.public_id}`} className="underline">
              View booking details
            </Link>
          </div>
        ) : null}

        {bookingError ? (
          <div className="mb-3 rounded-xl border border-red-500/35 bg-red-500/10 p-2 text-xs text-red-200">{bookingError}</div>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-[1.05fr_1fr]">
          <div className="space-y-3">
            <div className="rounded-xl border border-white/10 bg-black/20 p-3">
              <div className="mb-2 text-xs font-semibold text-white/70">Service</div>
              <select
                value={serviceId ?? ''}
                onChange={(e) => setServiceId(e.target.value ? Number(e.target.value) : null)}
                className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white"
              >
                {services.length === 0 ? <option value="">No public services</option> : null}
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                    {' · '}
                    {service.duration_minutes}
                    {'m'}
                    {service.price_cents != null ? ` · $${(service.price_cents / 100).toFixed(2)}` : ''}
                  </option>
                ))}
              </select>
            </div>

            {staffList.length > 0 ? (
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <div className="mb-2 text-xs font-semibold text-white/70">Staff</div>
                <div className="mb-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setStaffMode('ANY')}
                    className={`rounded-full border px-3 py-1 text-xs ${staffMode === 'ANY' ? 'border-brand/60 bg-brand/20 text-brand-100' : 'border-white/15 bg-white/5 text-white/75'}`}
                  >
                    Any
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStaffMode('SPECIFIC')
                      setStaffId((prev) => prev ?? staffList[0]?.id ?? null)
                    }}
                    className={`rounded-full border px-3 py-1 text-xs ${staffMode === 'SPECIFIC' ? 'border-brand/60 bg-brand/20 text-brand-100' : 'border-white/15 bg-white/5 text-white/75'}`}
                  >
                    Specific
                  </button>
                </div>
                {staffMode === 'SPECIFIC' ? (
                  <select
                    value={staffId ?? ''}
                    onChange={(e) => setStaffId(e.target.value ? Number(e.target.value) : null)}
                    className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white"
                  >
                    {staffList.map((staff) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.user.display_name || staff.user.handle}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
            ) : null}

            <BookingCalendar
              selectedDate={selectedDate}
              visibleMonth={visibleMonth}
              onVisibleMonthChange={setVisibleMonth}
              onSelectDate={setSelectedDate}
              daySummaries={daySummaries}
            />

            <div className="flex justify-end">
              <NextAvailableButton loading={findingNext} onFind={findNextAvailable} />
            </div>
          </div>

          <div className="space-y-3">
            <div className="rounded-xl border border-white/10 bg-black/20 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-xs font-semibold text-white/70">
                  {new Date(`${selectedDate}T12:00:00`).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}
                </div>
                {availabilityLoading ? <div className="text-xs text-white/50">Loading...</div> : null}
              </div>
              <BookingDaySlots
                slots={slots}
                onPick={(slot) => {
                  setSelectedSlot(slot)
                  setBookingError(null)
                }}
              />
            </div>

            <div className="rounded-xl border border-white/10 bg-black/20 p-3">
              <div className="mb-2 text-xs font-semibold text-white/70">Details</div>
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name *"
                  className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white"
                />
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email *"
                  className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white"
                />
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone (optional)"
                  className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white"
                />
                <input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notes (optional)"
                  className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white"
                />
              </div>
            </div>

            {selectedSlot ? (
              <div className="rounded-xl border border-white/10 bg-black/20 p-3 text-xs text-white/80">
                <div>
                  Selected:
                  {' '}
                  {new Date(selectedSlot.starts_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </div>
                {bookingQuote ? (
                  <div className="mt-1 text-white/65">
                    {bookingQuote.deposit_amount_cents > 0
                      ? `Deposit due now: $${(bookingQuote.deposit_amount_cents / 100).toFixed(2)}`
                      : 'No deposit required'}
                  </div>
                ) : null}
              </div>
            ) : null}

            {bookingClientSecret ? (
              stripePromise ? (
                <div className="rounded-xl border border-brand/40 bg-brand/10 p-3">
                  <div className="mb-2 text-xs font-semibold text-brand-100">Complete payment to confirm</div>
                  <Elements stripe={stripePromise} options={{ clientSecret: bookingClientSecret, appearance: { theme: 'night' as const } }}>
                    <DiscoverBookingPaymentConfirm
                      busy={paymentSubmitting}
                      setBusy={setPaymentSubmitting}
                      onError={(msg) => setBookingError(msg)}
                      onSuccess={onPaymentSuccess}
                    />
                  </Elements>
                </div>
              ) : (
                <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-2 text-xs text-yellow-200">
                  Stripe is not configured in this environment.
                </div>
              )
            ) : (
              <button
                type="button"
                onClick={onSubmit}
                disabled={!canSubmit || bookingSubmitting || Boolean(bookingSuccess)}
                className="w-full rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
              >
                {bookingSubmitting ? 'Booking...' : bookingQuote?.deposit_amount_cents ? 'Continue to payment' : 'Confirm booking'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
